import type { RailAction, RailState } from './railState';

/**
 * The HUD: a resources pill top-left and a command rail down the right edge (the layout
 * Rick chose for #10). Plain HTML inside #hud, so it respects the safe areas and gets
 * real 44-point buttons. Absolutely positioned elements ignore their parent's padding,
 * so every element applies env(safe-area-inset-*) itself.
 *
 * The rail captures its own touches (pointer-events: auto), so taps on it never reach
 * the map. DOM is rebuilt only when the rail state actually changes.
 */
export interface Hud {
  setResources(gold: number, lumber: number, supplyUsed: number, supplyCap: number): void;
  setRail(state: RailState, selectArmed: boolean): void;
  /** Dev overlay text (top-right of the map area). Pass null to hide. */
  setDebug(text: string | null): void;
  /** Current rail width in CSS px (includes the right safe-area inset). */
  railWidth(): number;
}

export interface HudHandlers {
  onAction(action: RailAction): void;
  onSelect(): void;
  onStop(): void;
}

/** Rail width before the right safe-area inset is added. */
export const RAIL_WIDTH_PX = 104;

const INSET_TOP = 'env(safe-area-inset-top, 0px)';
const INSET_LEFT = 'env(safe-area-inset-left, 0px)';
const INSET_RIGHT = 'env(safe-area-inset-right, 0px)';
const INSET_BOTTOM = 'env(safe-area-inset-bottom, 0px)';

const BUTTON_CSS =
  'display:block;width:100%;min-height:44px;margin:0 0 6px;padding:6px 6px;font:600 13px/1.2 sans-serif;' +
  'border:0;border-radius:8px;color:#1b1408;background:#e0b24c;touch-action:manipulation;white-space:normal;';

export function createHud(
  handlers: HudHandlers,
  root: HTMLElement | null = document.getElementById('hud'),
): Hud {
  const bar = document.createElement('div');
  bar.setAttribute('data-testid', 'resources');
  bar.style.cssText =
    `position:absolute;top:calc(${INSET_TOP} + 6px);left:calc(${INSET_LEFT} + 10px);` +
    'font:600 16px/1 sans-serif;color:#f2e8c9;background:rgba(0,0,0,.45);padding:6px 10px;' +
    'border-radius:8px;pointer-events:none;';
  root?.appendChild(bar);

  const rail = document.createElement('div');
  rail.setAttribute('data-testid', 'rail');
  rail.style.cssText =
    `position:absolute;top:0;right:0;bottom:0;width:calc(${RAIL_WIDTH_PX}px + ${INSET_RIGHT});` +
    `padding:calc(${INSET_TOP} + 6px) calc(${INSET_RIGHT} + 6px) calc(${INSET_BOTTOM} + 6px) 6px;` +
    'box-sizing:border-box;display:flex;flex-direction:column;background:rgba(0,0,0,.55);' +
    'color:#f2e8c9;font:14px/1.3 sans-serif;pointer-events:auto;overflow:hidden;';
  root?.appendChild(rail);

  const panel = document.createElement('div');
  panel.setAttribute('data-testid', 'panel');
  panel.style.cssText = 'flex:1 1 auto;min-height:0;overflow-y:auto;';
  rail.appendChild(panel);

  const footer = document.createElement('div');
  footer.style.cssText = 'flex:0 0 auto;padding-top:6px;';
  rail.appendChild(footer);

  const select = document.createElement('button');
  select.type = 'button';
  select.setAttribute('data-testid', 'select-mode');
  select.style.cssText = BUTTON_CSS + 'background:#a8c9a0;';
  select.addEventListener('click', () => handlers.onSelect());
  footer.appendChild(select);

  const stop = document.createElement('button');
  stop.type = 'button';
  stop.setAttribute('data-testid', 'stop-all');
  stop.textContent = 'Stop';
  stop.style.cssText = BUTTON_CSS + 'background:#d9a08a;margin-bottom:0;';
  stop.addEventListener('click', () => handlers.onStop());
  footer.appendChild(stop);

  const debug = document.createElement('pre');
  debug.setAttribute('data-testid', 'debug');
  debug.style.cssText =
    `position:absolute;top:calc(${INSET_TOP} + 6px);right:calc(${RAIL_WIDTH_PX}px + ${INSET_RIGHT} + 10px);` +
    'margin:0;font:12px/1.3 monospace;color:#f2e8c9;background:rgba(0,0,0,.55);padding:6px 8px;' +
    'border-radius:8px;pointer-events:none;display:none;white-space:pre;';
  root?.appendChild(debug);

  let lastBar = '';
  let lastPanelKey = '';
  let lastFooterKey = '';
  let lastDebug: string | null = null;
  const actions = new Map<string, RailAction>();

  function renderPanel(state: RailState): void {
    panel.replaceChildren();
    if (state.card) {
      const title = document.createElement('div');
      title.textContent = state.card.title;
      title.style.cssText = 'font-weight:700;font-size:15px;margin-bottom:4px;';
      panel.appendChild(title);
      for (const line of state.card.lines) {
        const el = document.createElement('div');
        el.textContent = line;
        el.style.cssText = 'margin-bottom:2px;';
        panel.appendChild(el);
      }
      if (state.card.progress !== null) {
        const track = document.createElement('div');
        track.style.cssText =
          'height:8px;background:rgba(255,255,255,.2);border-radius:4px;margin:6px 0;';
        const fill = document.createElement('div');
        fill.setAttribute('data-testid', 'progress');
        fill.style.cssText = `height:100%;width:${Math.round(state.card.progress * 100)}%;background:#e0b24c;border-radius:4px;`;
        track.appendChild(fill);
        panel.appendChild(track);
      }
      const gap = document.createElement('div');
      gap.style.cssText = 'height:8px;';
      panel.appendChild(gap);
    }
    for (const b of state.buttons) {
      const btn = document.createElement('button');
      btn.type = 'button';
      btn.textContent = b.label;
      btn.disabled = !b.enabled;
      btn.setAttribute('data-testid', b.id);
      btn.style.cssText = BUTTON_CSS + (b.enabled ? '' : 'opacity:.45;');
      btn.addEventListener('click', () => {
        const a = actions.get(b.id);
        if (a) handlers.onAction(a);
      });
      panel.appendChild(btn);
    }
  }

  return {
    setResources(gold, lumber, supplyUsed, supplyCap) {
      const text = `Gold ${gold}   Lumber ${lumber}   Supply ${supplyUsed}/${supplyCap}`;
      if (text !== lastBar) {
        bar.textContent = text;
        lastBar = text;
      }
    },
    setRail(state, selectArmed) {
      actions.clear();
      for (const b of state.buttons) actions.set(b.id, b.action);
      const key = JSON.stringify([
        state.card?.title,
        state.card?.lines,
        state.card?.progress === null || state.card?.progress === undefined
          ? null
          : Math.round(state.card.progress * 100),
        state.buttons.map((b) => [b.id, b.label, b.enabled]),
      ]);
      if (key !== lastPanelKey) {
        lastPanelKey = key;
        renderPanel(state);
      }
      const fkey = `${selectArmed}|${state.stopEnabled}`;
      if (fkey !== lastFooterKey) {
        lastFooterKey = fkey;
        select.textContent = selectArmed ? 'Drag a box…' : 'Select';
        select.style.background = selectArmed ? '#e0b24c' : '#a8c9a0';
        stop.disabled = !state.stopEnabled;
        stop.style.opacity = state.stopEnabled ? '1' : '.45';
      }
    },
    setDebug(text) {
      if (text === lastDebug) return;
      lastDebug = text;
      debug.style.display = text === null ? 'none' : 'block';
      debug.textContent = text ?? '';
    },
    railWidth() {
      return rail.offsetWidth || RAIL_WIDTH_PX;
    },
  };
}
